import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import useSystemStore from "@store/useSystemStore";
import useMeetingStore from "@store/useMeetingStore";
import usePlatformStore from "@store/usePlatformStore";
import { useUsers } from "@components/TeamList/hooks/useUsers";
import meetingService from "@services/meeting.service";
import { MEETING_QUERY_KEY, QUERY_CACHE_DEFAULT_EXPIRATION_MS } from "@config/query";
import { ItemType } from "@enums/common.enum";

export function useGetMeeting(enabled: boolean = true) {
  const { itemId, itemType, calendarId } = usePlatformStore();
  const { setMeetingId } = useMeetingStore();
  const { isAppActive } = useSystemStore();
  const { data: users } = useUsers();

  const isValidItem = !!itemId && itemType === ItemType.MEETING;

  const { data, error, isLoading, fetchStatus, isPlaceholderData, refetch } = useQuery({
    // itemType gate: the outgoing screen manager stays mounted through the transition, so
    // without it a calendar lookup fires for the newly selected email (and 400s).
    enabled: enabled && isAppActive && isValidItem,
    queryKey: [MEETING_QUERY_KEY, itemId],
    queryFn: async () => {
      return await meetingService.getMeeting(itemId, calendarId);
    },
    select: (response) => {
      // Ensure that null meeting is returned instead of an empty object
      if (!response?.meeting) {
        return { ...response, meeting: null };
      }

      // Enrich each attendee with full user data, keeping unmatched attendees (e.g. inactive users) as-is
      const attendees = (response.meeting?.attendees ?? []).map((attendee) => {
        if (attendee.external) return attendee;
        const enrichedUser = users?.results?.find((user) => user.id === attendee.id);
        return enrichedUser ?? attendee;
      });
      return {
        ...response,
        meeting: { ...response.meeting, attendees },
      };
    },
    staleTime: QUERY_CACHE_DEFAULT_EXPIRATION_MS,
    retry: false,
  });

  useEffect(() => {
    setMeetingId(data?.meeting?.id ?? null);
  }, [data?.meeting?.id, setMeetingId]);

  return { data, error, isLoading, fetchStatus, isPlaceholderData, refetch };
}
